import type { FC } from 'react';

import { cn } from '@pesto/utils';

import type { InputPestoProps } from './types/Input.types';

type TextFieldWrapperProps = Omit<InputPestoProps, 'readonly'> & {
  children: React.ReactNode;
  readOnly?: boolean;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
};

export const TextFieldWrapper: FC<TextFieldWrapperProps> = (props) => {
  const {
    className,
    children,
    label,
    error,
    readOnly,
    helperText,
    description,
    required,
    labelClassName,
    id: providedId,
    name,
  } = props;

  const id = providedId || (name ? `form-field-${name}` : undefined);
  const helperId = id ? `${id}-helper` : undefined;
  const errorId = id ? `${id}-error` : undefined;
  const descriptionId = id ? `${id}-description` : undefined;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <div className="flex justify-between">
          {readOnly ? (
            <label
              htmlFor={id}
              className={cn('text-muted-foreground text-xs font-medium uppercase tracking-wide', labelClassName)}
            >
              {label}
            </label>
          ) : (
            <label
              htmlFor={id}
              className={cn(
                'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                error && 'text-destructive',
                labelClassName,
              )}
            >
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </label>
          )}
          {description && (
            <span id={descriptionId} className="text-muted-foreground text-xs">
              {description}
            </span>
          )}
        </div>
      )}
      {children}
      {(helperText || error) && (
        <div className="space-y-1">
          {helperText && (
            <p id={helperId} className="text-muted-foreground text-sm">
              {helperText}
            </p>
          )}
          {error && (
            <p id={errorId} className="text-destructive text-sm">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
